import { useState, useEffect, useCallback, useRef } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { paymentDialogAPI, uploadAPI, BASE_URL } from '../services/api';
import FilterBar, { SearchField, FilterSelect, CountPill } from '../components/FilterBar';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  thRight: { textAlign: 'right' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'top' },
  tdRight: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', verticalAlign: 'top' },
  thumb: { width: '64px', height: '40px', borderRadius: '8px', objectFit: 'cover', backgroundColor: '#f1f5f9', display: 'block' },
  thumbPlaceholder: { width: '64px', height: '40px', borderRadius: '8px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '10px' },
  descCell: { color: '#475569', maxWidth: '380px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' },
  buttonText: { fontWeight: '600', color: '#1e293b' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer', display: 'inline-block' },
  activeStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  inactiveStatus: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actionButtons: { display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' },
  iconButton: { padding: '7px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer', color: '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' },
  emptyState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  loadingState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', margin: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  // Modal
  modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 },
  modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#fafbfc' },
  closeBtn: { width: '32px', height: '32px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', minHeight: '90px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  uploadRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  uploadPreview: { width: '120px', height: '72px', borderRadius: '10px', objectFit: 'cover', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' },
  uploadActions: { display: 'flex', flexDirection: 'column', gap: '6px' },
  smallBtn: { padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', color: '#475569', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  smallBtnDanger: { padding: '7px 12px', border: '1px solid #fecaca', borderRadius: '8px', backgroundColor: '#fff', color: '#dc2626', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  toggleRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569', cursor: 'pointer' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  secondaryButton: { padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
};

const defaultForm = { type: '', imageUrl: '', buttonText: '', description: '', amount: '', privacyPolicyUrl: '', sortOrder: 0, isActive: true };

const filenameFromUrl = (url) => {
  if (!url) return '';
  const m = String(url).match(/payment-dialog-[^/?#]+/);
  return m ? m[0] : '';
};

const toAbsolute = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const PaymentDialogs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter === 'active';
      const response = await paymentDialogAPI.getAll(params);
      const d = response.data || {};
      setItems(Array.isArray(d.data) ? d.data : []);
      setTotalPages(d.pagination?.totalPages || 1);
      setTotal(d.pagination?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch payment dialogs');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setPage]);

  // Close modal on Escape
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...defaultForm });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      type: item.type || '',
      imageUrl: item.imageUrl || '',
      buttonText: item.buttonText || '',
      description: item.description || '',
      amount: item.amount != null ? String(item.amount) : '',
      privacyPolicyUrl: item.privacyPolicyUrl || '',
      sortOrder: item.sortOrder || 0,
      isActive: !!item.isActive,
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormError('');
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image must be 5 MB or smaller.');
      return;
    }
    setUploading(true);
    setFormError('');
    try {
      const res = await uploadAPI.uploadPaymentDialogImage(file);
      // Prefer the portable relativeUrl so the same DB row works across environments.
      const url = res?.data?.relativeUrl || res?.data?.url;
      if (!url) throw new Error('Upload returned no URL');
      setForm(f => ({ ...f, imageUrl: url }));
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async () => {
    const fname = filenameFromUrl(form.imageUrl);
    if (fname) {
      try { await uploadAPI.deletePaymentDialogImage(fname); } catch { /* ignore */ }
    }
    setForm(f => ({ ...f, imageUrl: '' }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!String(form.type ?? '').trim() || !form.buttonText.trim() || !form.description.trim()) {
      setFormError('Type, button text and description are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: String(form.type ?? '').trim(),
        imageUrl: form.imageUrl || null,
        buttonText: form.buttonText.trim(),
        description: form.description.trim(),
        amount: String(form.amount ?? '').trim() || null,
        privacyPolicyUrl: String(form.privacyPolicyUrl ?? '').trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: !!form.isActive,
      };
      if (editing) {
        await paymentDialogAPI.update(editing.id, payload);
      } else {
        await paymentDialogAPI.create(payload);
      }
      closeModal();
      fetchItems();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await paymentDialogAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      fetchItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
      setConfirmDelete(null);
    }
  };

  const toggleStatus = async (item) => {
    try {
      await paymentDialogAPI.update(item.id, { isActive: !item.isActive });
      fetchItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Payment Dialogs</h1>
          <p style={styles.subtitle}>Image + button + description cards shown in the mobile app's payment flow.</p>
        </div>
        <button style={styles.addButton} onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          New Dialog
        </button>
      </div>

      <div style={styles.card}>
        <FilterBar right={<CountPill count={total} noun="dialog" />}>
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search by button text or description..."
            width={320}
          />
          <FilterSelect value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} aria-label="Status">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </FilterSelect>
        </FilterBar>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.loadingState}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '15px', margin: 0 }}>No payment dialogs yet.</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>Click "New Dialog" to add the first one.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Image</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Button Text</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      {item.imageUrl ? (
                        <img src={toAbsolute(item.imageUrl)} alt="" style={styles.thumb} />
                      ) : (
                        <div style={styles.thumbPlaceholder}>—</div>
                      )}
                    </td>
                    <td style={styles.td}>{item.type || <span style={{ color: '#ef4444' }}>—</span>}</td>
                    <td style={{ ...styles.td, ...styles.buttonText }}>{item.buttonText}</td>
                    <td style={styles.td}>
                      <div style={styles.descCell}>{item.description}</div>
                    </td>
                    <td style={styles.td}>{item.sortOrder}</td>
                    <td style={styles.td}>
                      <span
                        style={{ ...styles.statusBadge, ...(item.isActive ? styles.activeStatus : styles.inactiveStatus) }}
                        onClick={() => toggleStatus(item)}
                        title="Click to toggle"
                      >
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.tdRight}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.iconButton}
                          title="Edit"
                          onClick={() => openEdit(item)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                        <button
                          style={styles.iconButton}
                          title="Delete"
                          onClick={() => setConfirmDelete(item)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && items.length > 0 && totalPages > 1 && (
          <div style={styles.pagination}>
            <span style={styles.pageInfo}>Page {page} of {totalPages}</span>
            <div style={styles.pageButtons}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }}
              >Prev</button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit modal */}
      {showModal && (
        <div style={styles.modal} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{editing ? 'Edit Payment Dialog' : 'New Payment Dialog'}</h2>
                <button type="button" onClick={closeModal} style={styles.closeBtn}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>

              <div style={styles.modalBody}>
                {formError && (
                  <div style={{ ...styles.errorBox, margin: '0 0 16px 0' }}>{formError}</div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Image</label>
                  <div style={styles.uploadRow}>
                    {form.imageUrl ? (
                      <img src={toAbsolute(form.imageUrl)} alt="preview" style={styles.uploadPreview} />
                    ) : (
                      <div style={{ ...styles.uploadPreview, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '11px' }}>No image</div>
                    )}
                    <div style={styles.uploadActions}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImagePick}
                        style={{ display: 'none' }}
                      />
                      <button type="button" style={styles.smallBtn} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                        {uploading ? 'Uploading…' : form.imageUrl ? 'Replace Image' : 'Upload Image'}
                      </button>
                      {form.imageUrl && (
                        <button type="button" style={styles.smallBtnDanger} onClick={removeImage}>
                          Remove Image
                        </button>
                      )}
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>PNG, JPG, WebP. Max 5 MB.</span>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Type *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.type}
                    onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
                    placeholder="e.g. CHALLAN, RC_SEARCH, PUC, INSURANCE, VALUATION"
                    maxLength={80}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Button Text *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.buttonText}
                    onChange={(e) => setForm(f => ({ ...f, buttonText: e.target.value }))}
                    placeholder="e.g. Continue to Payment"
                    maxLength={200}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description *</label>
                  <textarea
                    style={styles.textarea}
                    value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Short message shown above/around the button..."
                    rows={5}
                    required
                  />
                </div>

                <div style={styles.twoCol}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={styles.input}
                      value={form.amount}
                      onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="e.g. 10 or 499.00"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Privacy Policy Link</label>
                    <input
                      type="url"
                      style={styles.input}
                      value={form.privacyPolicyUrl}
                      onChange={(e) => setForm(f => ({ ...f, privacyPolicyUrl: e.target.value }))}
                      placeholder="https://vehiclesetu.com/payment-privacy-policy"
                      maxLength={1000}
                    />
                  </div>
                </div>

                <div style={styles.twoCol}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Sort Order</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={form.sortOrder}
                      onChange={(e) => setForm(f => ({ ...f, sortOrder: e.target.value }))}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <label style={styles.toggleRow}>
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                      />
                      <span>Active (visible in the mobile app)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={closeModal} style={styles.secondaryButton}>Cancel</button>
                <button
                  type="submit"
                  disabled={saving || uploading}
                  style={{ ...styles.primaryButton, ...((saving || uploading) ? styles.buttonDisabled : {}) }}
                >
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Dialog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div style={styles.modal} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Delete Dialog?</h2>
            </div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                This will permanently delete the dialog "<strong>{confirmDelete.buttonText}</strong>". This action cannot be undone.
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={styles.secondaryButton}>Cancel</button>
              <button
                type="button"
                onClick={handleDelete}
                style={{ ...styles.primaryButton, backgroundColor: '#dc2626' }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentDialogs;
